import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import { GlobalStyles } from '@experiences/theme';
import { UiText } from '@experiences/ui-common';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import FileSaver from 'file-saver';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';

import { notificationType } from '../../../common/constants/Constant';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import type {
    IActivateOfflineData,
    IOfflineActivationRequestResponse,
} from '../../../common/interfaces/license.activation';
import { getActivationRequest } from '../../../services/licensing/management/ActivationService';
import { isHostModeSelector } from '../../../store/selectors';
import validateLicenseCode from '../../../util/validators/LicenseCodeValidator';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        stepInstruction: {
            color: theme.palette.semantic.colorForeground,
            fontWeight: 'bold',
            fontSize: '14px',
        },
        stepper: { paddingLeft: '0px' },
    }),
}));

const LicenseActivationStepsComponent: React.FC<{ isTrialPerSkuActivation: boolean }> = ({ isTrialPerSkuActivation }) => {
    const classes = useStyles();
    const setErrorMessage = useCentralErrorSetter();
    const [ downloadInProgress, setDownloadInProgress ] = useState(false);
    const createNotification = useUiSnackBar();
    const { formatMessage: translate } = useIntl();
    const {
        getValues, register, setError,
    } = useFormContext<IActivateOfflineData>();
    const isHostMode = useSelector(isHostModeSelector);
    const { getErrorMessage } = useGetErrorInfo();

    const saveActivationRequestFile = useCallback(
        (downloadedActivationRequestData: IOfflineActivationRequestResponse, filename: string) => {
            const downloadedActivationRequest = new Blob([ downloadedActivationRequestData.activationRequest ], { type: 'charset=utf-8' });
            FileSaver.saveAs(downloadedActivationRequest, filename);
        },
        [],
    );
    const downloadActivationRequest = useCallback(
        async (licenseCode: string) => {
            if (!validateLicenseCode(licenseCode)) {
                setError('licenseCode', { type: 'license-format' });
                return;
            }
            try {
                if (!downloadInProgress) {
                    setDownloadInProgress(true);
                    createNotification(
                        translate({ id: 'CLIENT_DOWNLOAD_IN_PROGRESS' }),
                        notificationType.INPROGRESS,
                    );
                    const activationRequestObject: IOfflineActivationRequestResponse = await getActivationRequest(
                        licenseCode,
                        isHostMode,
                        isTrialPerSkuActivation,
                    );
                    saveActivationRequestFile(activationRequestObject, translate({ id: 'CLIENT_ACTIVATION_REQUEST' }));
                }
            } catch (error) {
                setErrorMessage(await getErrorMessage(error));
            } finally {
                setDownloadInProgress(false);
            }
        },
        [
            setError,
            downloadInProgress,
            createNotification,
            translate,
            isHostMode,
            isTrialPerSkuActivation,
            saveActivationRequestFile,
            setErrorMessage,
            getErrorMessage,
        ],
    );

    const steps = useMemo(() => [
        translate({ id: 'CLIENT_DOWNLOAD_ACTIVATION_REQUEST' }),
        translate({ id: 'CLIENT_ACTIVATE_YOUR_LICENSE' }),
        translate({ id: 'CLIENT_DOWNLOAD_ACTIVATION_FILE' }),
        translate({ id: 'CLIENT_UPLOAD_LICENSE_HERE' }),
    ], [ translate ]);

    const stepsContent = useMemo(() => [
        <Button
            onClick={() => downloadActivationRequest(getValues('licenseCode'))}
            key={1}
            variant="outlined"
            data-cy="download-activation-request"
        >
            {translate({ id: 'CLIENT_DOWNLOAD' })}
        </Button>,
        <FormattedMessage
            key={2}
            id="CLIENT_GO_TO_ACTIVATION_PORTAL"
            values={{
                a: (msg: React.ReactNode[]) =>
                    (
                        <a
                            className={classes.a}
                            href="https://activate.uipath.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {msg}
                        </a>
                    ),
            }}
        />,
        '',
        <input
            key={4}
            type="file"
            {...register('file')}
            required
            data-cy="file-input" />,
    ], [ downloadActivationRequest, getValues, register, translate, classes ]);

    return (
        <Stepper
            orientation="vertical"
            className={classes.stepper}>
            {steps.map((label, index) => (
                <Step
                    key={label}
                    active>
                    <StepLabel>
                        <div className={classes.stepInstruction}>
                            {label}
                        </div>
                    </StepLabel>
                    <StepContent>
                        <UiText>
                            {stepsContent[index]}
                        </UiText>
                    </StepContent>
                </Step>
            ))}
        </Stepper>
    );
};
export default LicenseActivationStepsComponent;
